// src/components/EventModal.js
// Updated: 11/17/2025 01:42 PM

import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Modal, Box, Typography, TextField, Button, Checkbox, FormControlLabel, Select, MenuItem, InputLabel, FormControl, CircularProgress, IconButton, Switch, Link, Chip } from '@mui/material';
import { LocalizationProvider, DateTimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { doc, writeBatch, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { format as formatFns, addDays, isValid as dateFnsIsValid } from 'date-fns';
import { uploadFile, deleteFileByUrl } from '../services/storageService';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/HourglassEmpty';


const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

// Helper for days of week for RRULE
const DAYS_OF_WEEK = [
  { label: 'Sunday', value: 'SU' },
  { label: 'Monday', value: 'MO' },
  { label: 'Tuesday', value: 'TU' },
  { label: 'Wednesday', value: 'WE' },
  { label: 'Thursday', value: 'TH' },
  { label: 'Friday', value: 'FR' },
  { label: 'Saturday', value: 'SA' },
];


// NEW HELPER FUNCTION: Image Resizer
const resizeImage = (file, maxWidth, maxHeight, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions to fit within maxWidth/maxHeight
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert canvas to Blob (PNG for transparency, JPEG for smaller size if no transparency needed)
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a new File object from the blob, preserving the original file name
            resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        }, file.type, quality); // Use original file type and specified quality
      };
    };
    reader.onerror = (error) => reject(error);
  });
};

const escapeICSString = (str) => {
    if (!str) return '';
    return str.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');
};


function EventModal({ open, onClose, onSave, onDelete, event, isLeadership, churchMembers, isEventRegistrationEnabledForChurch, selectedOccurrenceStart, churchProfile }) {
  const { currentUser, userProfile } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startAt, setStartAt] = useState(null);
  const [endAt, setEndAt] = useState(null);
  const [location, setLocation] = useState('');
  const [virtualLink, setVirtualLink] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const [recurrenceRules, setRecurrenceRules] = useState([]);

  // NEW Recurrence States
  const [isRepeating, setIsRepeating] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState('weekly');
  const [repeatDaysOfWeek, setRepeatDaysOfWeek] = useState([]);
  const [repeatEndsAt, setRepeatEndsAt] = useState(null);
  const [monthlyWeek, setMonthlyWeek] = useState(0); // NEW: State for week number (0 = every, 1 = 1st, 2 = 2nd, etc.)

  // Registration states
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(false);
  const [capacity, setCapacity] = useState('');
  const [registrationDeadline, setRegistrationDeadline] = useState(null);
  const [isPaidEvent, setIsPaidEvent] = useState(false);
  const [ticketPrice, setTicketPrice] = useState('');
  const [eventPOCs, setEventPOCs] = useState([]);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);

  const [isRegistering, setIsRegistering] = useState(false);
  // MODIFIED: Now stores the full registration object for the current user
  const [currentUserRegistrationStatus, setCurrentUserRegistrationStatus] = useState(null);

  // NEW STATES FOR GALLERY VIEW & FEATURED EVENTS
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);


  // Define specific roles for clarity
  const isPOC = event?.eventPOCs?.includes(currentUser?.uid) || false;
  const isAdmin = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';


  useEffect(() => {
    if (event) {
      setTitle(event.title || '');
      setDescription(event.description || '');
      setStartAt(event.startDateTime || null);
      setEndAt(event.endDateTime || null);
      setLocation(event.location || '');
      setVirtualLink(event.virtualLink || '');
      setIsAllDay(event.isAllDay || false);
      setAttendees(event.attendees || []);
      setRecurrenceRules(event.recurrenceRules || []);

      // Initialize imageUrl and isFeatured
      setCurrentImageUrl(event.imageUrl || '');
      setIsFeatured(event.isFeatured || false);


      // NEW: Initialize recurrence states from existing event recurrenceRules
      if (event.recurrenceRules && event.recurrenceRules.length > 0) {
        setIsRepeating(true);
        const rruleString = event.recurrenceRules[0];

        // *** START OF FIX: Detect Biweekly Rule ***
        if (rruleString.includes('FREQ=WEEKLY')) {
          if (rruleString.includes('INTERVAL=2')) {
            setRepeatFrequency('biweekly');
          } else {
            setRepeatFrequency('weekly');
          }
          const bydayMatch = rruleString.match(/BYDAY=([A-Z,]+)/);
          if (bydayMatch) {
            setRepeatDaysOfWeek(bydayMatch[1].split(','));
          } else {
            if (event.startDateTime) {
              setRepeatDaysOfWeek([formatFns(event.startDateTime, 'EEEEEE').toUpperCase()]);
            } else {
              setRepeatDaysOfWeek([]);
            }
          }
        // *** END OF FIX ***
        } else if (rruleString.includes('FREQ=MONTHLY')) {
          setRepeatFrequency('monthly');
          // NEW: Initialize monthlyWeek from BYSETPOS
          const bysetposMatch = rruleString.match(/BYSETPOS=([0-9-]+)/);
          if (bysetposMatch) {
            setMonthlyWeek(parseInt(bysetposMatch[1], 10));
          } else {
            setMonthlyWeek(0);
          }
        } else if (rruleString.includes('FREQ=YEARLY')) {
          setRepeatFrequency('yearly');
        } else {
          setRepeatFrequency('weekly');
        }

        const untilMatch = rruleString.match(/UNTIL=([0-9TZ]+)/);
        if (untilMatch) {
          try {
            // MODIFIED: Robust parsing of UNTIL date
            const untilString = untilMatch[1];
            const year = parseInt(untilString.substring(0, 4));
            const month = parseInt(untilString.substring(4, 6)) - 1;
            const day = parseInt(untilString.substring(6, 8));
            const hour = parseInt(untilString.substring(9, 11));
            const minute = parseInt(untilString.substring(11, 13));
            const second = parseInt(untilString.substring(13, 15));
            const untilDate = new Date(Date.UTC(year, month, day, hour, minute, second));
            setRepeatEndsAt(untilDate);
          } catch (e) {
            console.error("Error parsing UNTIL date from RRULE:", e);
            setRepeatEndsAt(null);
          }
        } else {
          setRepeatEndsAt(null);
        }
      } else {
        setIsRepeating(false);
        setRepeatFrequency('weekly');
        setRepeatDaysOfWeek([]);
        setRepeatEndsAt(null);
        setMonthlyWeek(0); // NEW: Reset monthly week
      }


      setIsRegistrationEnabled(event.isRegistrationEnabled || false);
      setCapacity(event.capacity || '');
      setRegistrationDeadline(event.registrationDeadline ? event.registrationDeadline.toDate() : null);
      setIsPaidEvent(event.isPaidEvent || false);
      setTicketPrice(event.ticketPrice || '');
      setEventPOCs(event.eventPOCs || []);
      setCurrentRegistrations(event.currentRegistrations || 0);

      // NEW: Fetch current user's registration status when event changes
      const fetchCurrentUserRegistration = async () => {
        if (currentUser && userProfile?.churchId && event.id) {
          const registrationDocRef = doc(db, 'churches', userProfile.churchId, 'events', event.id, 'registrations', currentUser.uid);
          const docSnap = await getDoc(registrationDocRef);
          if (docSnap.exists()) {
            setCurrentUserRegistrationStatus(docSnap.data());
          } else {
            setCurrentUserRegistrationStatus(null);
          }
        } else {
          setCurrentUserRegistrationStatus(null);
        }
      };
      fetchCurrentUserRegistration();

    } else {
      setTitle('');
      setDescription('');
      setStartAt(null);
      setEndAt(null);
      setLocation('');
      setVirtualLink('');
      setIsAllDay(false);
      setAttendees([]);
      setRecurrenceRules([]);

      // NEW: Reset imageUrl and isFeatured for new event
      setSelectedImageFile(null);
      setCurrentImageUrl('');
      setIsFeatured(false);


      // NEW: Reset recurrence states for new event
      setIsRepeating(false);
      setRepeatFrequency('weekly');
      setRepeatDaysOfWeek([]);
      setRepeatEndsAt(null);
      setMonthlyWeek(0); // NEW: Reset monthly week

      setIsRegistrationEnabled(false);
      setCapacity('');
      setRegistrationDeadline(null);
      setIsPaidEvent(false);
      setTicketPrice('');
      setEventPOCs([]);
      setCurrentRegistrations(0);
      setCurrentUserRegistrationStatus(null); // Reset for new event
    }
    setUploadingImage(false); // Reset upload state
  }, [event, open, currentUser, userProfile]);

  // NEW: Handle image file selection
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) { // Ensure it's an image
      setSelectedImageFile(file);
      setCurrentImageUrl(URL.createObjectURL(file)); // Create a preview URL
    } else {
      setSelectedImageFile(null);
      // If no file selected or not an image, revert to original image URL or empty
      setCurrentImageUrl(event?.imageUrl || '');
      alert("Please select an image file.");
    }
    // Clear the input value so the same file can be selected again after removal
    e.target.value = null;
  };

  // NEW: Handle image removal
  const handleRemoveImage = () => {
    setSelectedImageFile(null);
    setCurrentImageUrl('');
  };


  const handleSave = async () => {
    if (!title || !startAt) {
      alert('Please fill out the Title and Start Date.');
      return;
    }
    let finalEndAt = endAt || startAt;

    if (isAllDay) {
        const start = new Date(startAt);
        start.setHours(0, 0, 0, 0);
        
        let end = new Date(finalEndAt);
        end.setHours(0, 0, 0, 0);

        if (end <= start) {
            end = addDays(start, 1);
        }
        finalEndAt = end;
    } else {
        if (finalEndAt < startAt) {
            finalEndAt = startAt;
        }
    }


    let generatedRecurrenceRules = [];
    if (isRepeating && startAt) {
      const churchTimeZone = churchProfile?.timeZone || 'America/New_York';
      // FIX: Added TZID parameter to the DTSTART portion of the RRULE string
      let rrule = `DTSTART;TZID=${churchTimeZone}:${formatFns(startAt, "yyyyMMdd'T'HHmmss")}\nRRULE:FREQ=${repeatFrequency.toUpperCase()}`;

      // *** START OF FIX: Handle Biweekly Rule ***
      if (repeatFrequency === 'biweekly') {
        rrule = rrule.replace('FREQ=BIWEEKLY', 'FREQ=WEEKLY;INTERVAL=2');
      }
      // *** END OF FIX ***

      if (repeatFrequency === 'monthly') {
          const startDayOfWeek = formatFns(startAt, 'EEEEEE').toUpperCase();
          if (monthlyWeek !== 0) {
              rrule += `;BYDAY=${monthlyWeek}${startDayOfWeek}`;
          }
      } else if (repeatFrequency === 'weekly') {
        const selectedDays = repeatDaysOfWeek.length > 0 ? repeatDaysOfWeek.sort().join(',') : formatFns(startAt, 'EEEEEE').toUpperCase();
        rrule += `;BYDAY=${selectedDays}`;
      }


      if (repeatEndsAt) {
        const untilUtc = new Date(repeatEndsAt.getTime());
        untilUtc.setUTCHours(23, 59, 59, 999);
        rrule += `;UNTIL=${formatFns(untilUtc, "yyyyMMdd'T'HHmmss'Z'")}`;
      }
      generatedRecurrenceRules.push(rrule);
    }


    if (isEventRegistrationEnabledForChurch && isRegistrationEnabled) {
        if (!capacity || parseInt(capacity) <= 0) {
            alert('Capacity must be a positive number if registration is enabled.');
            return;
        }
        if (isPaidEvent && (!ticketPrice || parseFloat(ticketPrice) <= 0)) {
            alert('Ticket Price must be a positive number if it is a paid event.');
            return;
        }
        if (eventPOCs.length === 0) {
            alert('At least one Event Point of Contact (POC) is required for registration-enabled events.');
            return;
        }
    }

    setUploadingImage(true);
    let finalImageUrl = currentImageUrl;

    if (selectedImageFile) {
        try {
            const resizedImage = await resizeImage(selectedImageFile, 800, 600, 0.75);
            const imagePath = `churches/${userProfile.churchId}/events/${event?.id || 'temp_event'}/${resizedImage.name}`;
            finalImageUrl = await uploadFile(resizedImage, imagePath);
            if (event?.imageUrl && event.imageUrl !== finalImageUrl) {
                await deleteFileByUrl(event.imageUrl);
            }
        } catch (error) {
            console.error("Error uploading event image:", error);
            alert("Failed to upload image. Please try again.");
            setUploadingImage(false);
            return;
        }
    } else if (event?.imageUrl && !currentImageUrl) {
        try {
            await deleteFileByUrl(event.imageUrl);
        } catch (error) {
            console.warn("Could not delete old event image from storage (was already removed?).", error);
        }
        finalImageUrl = '';
    }

    setUploadingImage(false);


    const eventData = {
      title, description, startAt, endAt: finalEndAt, location, virtualLink, isAllDay, attendees,
      recurrenceRules: generatedRecurrenceRules,
      exdates: event?.exdates || [],
      isRegistrationEnabled: isEventRegistrationEnabledForChurch ? isRegistrationEnabled : false,
      capacity: (isEventRegistrationEnabledForChurch && isRegistrationEnabled) ? parseInt(capacity) : null,
      registrationDeadline: (isEventRegistrationEnabledForChurch && isRegistrationEnabled)
                            ? (registrationDeadline || null)
                            : null,
      isPaidEvent: (isEventRegistrationEnabledForChurch && isRegistrationEnabled) ? isPaidEvent : false,
      ticketPrice: (isEventRegistrationEnabledForChurch && isRegistrationEnabled && isPaidEvent)
                   ? (isNaN(parseFloat(ticketPrice)) ? 0 : parseFloat(ticketPrice))
                   : null,
      eventPOCs: (isEventRegistrationEnabledForChurch && isRegistrationEnabled) ? eventPOCs : [],
      currentRegistrations: event?.currentRegistrations || 0,
      imageUrl: finalImageUrl,
      isFeatured: isFeatured,
    };
    onSave(eventData);
  };

  const formatDisplayDate = (date, allDay) => {
    if (!date) return 'Not specified';
    if (event?.isVolunteerEvent && date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0) {
        const options = { dateStyle: 'medium' };
        return new Date(date).toLocaleString([], options);
    }
    const options = allDay ? { dateStyle: 'medium', timeZone: 'UTC' } : { dateStyle: 'medium', timeStyle: 'short' };
    return new Date(date).toLocaleString([], options);
  };

  const handleAddToCalendar = () => {
    if (!event) return;

    if (!event.startDateTime || !dateFnsIsValid(new Date(event.startDateTime))) {
        alert('A valid start date/time is required to add this event to your calendar.');
        return;
    }

    const toICSDate = (date, allDay = false) => {
      const d = new Date(date);
      if (!dateFnsIsValid(d)) {
        return '';
      }

      if (allDay) {
        return d.toISOString().split('T')[0].replace(/-/g, '');
      } else {
        return d.toISOString().replace(/[-:.]/g, "").slice(0, -4) + 'Z';
      }
    };
    const startDateICS = event.startDateTime;
    const endDateICS = event.endDateTime || startDateICS;
    
    let dtstartString;
    let dtendString;

    if (event.isAllDay) {
      dtstartString = `DTSTART;VALUE=DATE:${toICSDate(startDateICS, true)}`;
      const actualEndDateForICS = addDays(new Date(endDateICS), 1);
      dtendString = `DTEND;VALUE=DATE:${toICSDate(actualEndDateForICS, true)}`;
    } else {
      dtstartString = `DTSTART:${toICSDate(startDateICS, false)}`;
      dtendString = `DTEND:${toICSDate(endDateICS, false)}`;
    }

    if (!dtstartString || !dtendString) {
      alert('Could not generate calendar data due to invalid date/time information.');
      return;
    }

    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Church Connect App//EN',
      'BEGIN:VEVENT',
      `UID:${event.id}@yourapp.com`,
      `DTSTAMP:${toICSDate(new Date())}`,
      dtstartString,
      dtendString,
      `SUMMARY:${escapeICSString(event.title || '')}`,
      `DESCRIPTION:${escapeICSString(event.description || '')}`,
      `LOCATION:${escapeICSString(event.location || '')}`,
    ];
    
    if (event.recurrenceRules && event.recurrenceRules.length > 0) {
      icsContent.push(`${event.recurrenceRules[0]}`);
    }
    icsContent.push('END:VEVENT', 'END:VCALENDAR');

    const finalIcsContent = icsContent.join('\n');

    const blob = new Blob([finalIcsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${event.title}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRegisterNow = async () => {
    if (!currentUser || !userProfile?.churchId || !event?.id) {
      alert("You must be logged in and associated with a church to register.");
      return;
    }
    setIsRegistering(true);
    try {
      const now = new Date();
      if (event.registrationDeadline && now > event.registrationDeadline.toDate()) {
        alert("Registration for this event has passed.");
        return;
      }
      if (event.currentRegistrations >= event.capacity) {
        alert("This event is full. No more registrations can be accepted.");
        return;
      }
      if (currentUserRegistrationStatus) {
        alert("You are already registered for this event.");
        return;
      }
      const batch = writeBatch(db);
      const eventDocRef = doc(db, 'churches', userProfile.churchId, 'events', event.id);
      const registrationDocRef = doc(db, 'churches', userProfile.churchId, 'events', event.id, 'registrations', currentUser.uid);
      batch.set(registrationDocRef, {
        userId: currentUser.uid,
        userName: `${userProfile.firstName} ${userProfile.lastName}`,
        registeredAt: serverTimestamp(),
        paymentStatus: event.isPaidEvent ? 'pending' : 'paid',
        ticketPrice: event.isPaidEvent ? event.ticketPrice : 0,
      });
      batch.update(eventDocRef, {
        currentRegistrations: event.currentRegistrations + 1,
      });
      await batch.commit();
      setCurrentUserRegistrationStatus({
        userId: currentUser.uid,
        userName: `${userProfile.firstName} ${userProfile.lastName}`,
        paymentStatus: event.isPaidEvent ? 'pending' : 'paid',
        ticketPrice: event.isPaidEvent ? event.ticketPrice : 0,
        attended: false,
        registeredAt: serverTimestamp(),
      });
      alert("Successfully registered for the event!");
    } catch (error) {
      console.error("Error during registration:", error);
      alert(`Failed to register: ${error.message}`);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleCancelRegistration = async () => {
    if (!currentUser || !userProfile?.churchId || !event?.id) return;

    if (currentUserRegistrationStatus && currentUserRegistrationStatus.paymentStatus === 'paid') {
      alert("Cannot cancel registration: You have already paid for this event.");
      return;
    }

    if (window.confirm("Are you sure you want to cancel your registration for this event?")) {
      setIsRegistering(true);
      try {
        const batch = writeBatch(db);
        const eventDocRef = doc(db, 'churches', userProfile.churchId, 'events', event.id);
        const registrationDocRef = doc(db, 'churches', userProfile.churchId, 'events', event.id, 'registrations', currentUser.uid);
        batch.delete(registrationDocRef);
        batch.update(eventDocRef, {
          currentRegistrations: event.currentRegistrations - 1,
        });
        await batch.commit();
        setCurrentUserRegistrationStatus(null);
        alert("Registration cancelled successfully.");
      } catch (error) {
        console.error("Error cancelling registration:", error);
        alert(`Failed to cancel registration: ${error.message}`);
      } finally {
        setIsRegistering(false);
      }
    }
  };

  const getPOCNames = (pocUids) => {
    if (!pocUids || pocUids.length === 0) return 'N/A';
    return pocUids.map(uid => {
      const member = churchMembers.find(m => m.id === uid);
      return member ? `${member.firstName} ${member.lastName}` : uid;
    }).join(', ');
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Typography variant="h6" component="h2" gutterBottom>
            {isLeadership && !event ? 'Create New Event' : 'Event Details'}
          </Typography>

          {isLeadership ? (
            <>
              <TextField margin="normal" fullWidth label="Event Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <FormControlLabel
                control={<Checkbox checked={isAllDay} onChange={(e) => setIsAllDay(e.target.checked)} />}
                label="All-day event"
              />
              <TextField margin="normal" fullWidth label="Description (Optional)" multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              <TextField margin="normal" fullWidth label="Location (Optional)" value={location} onChange={(e) => setLocation(e.target.value)} />
              <TextField margin="normal" fullWidth label="Virtual Link (Optional)" value={virtualLink} onChange={(e) => setVirtualLink(e.target.value)} />
              
              {isAllDay ? (
                <>
                  <DatePicker label="Start Date" value={startAt} onChange={(newValue) => setStartAt(newValue)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                    enableAccessibleFieldDOMStructure={false}
                  />
                  <DatePicker label="End Date (Optional)" value={endAt} onChange={(newValue) => setEndAt(newValue)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                    enableAccessibleFieldDOMStructure={false}
                  />
                </>
              ) : (
                <>
                  <DateTimePicker label="Start Time" value={startAt} onChange={(newValue) => setStartAt(newValue)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                    enableAccessibleFieldDOMStructure={false}
                  />
                  <DateTimePicker label="End Time (Optional)" value={endAt} onChange={(newValue) => setEndAt(newValue)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                    enableAccessibleFieldDOMStructure={false}
                  />
                </>
              )}

              {/* Event Image Upload */}
              <Box sx={{ mt: 2, border: '1px dashed grey', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom>Event Image (Optional)</Typography>
                {currentImageUrl ? (
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <img src={currentImageUrl} alt="Event Preview" style={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }} />
                    <IconButton size="small" color="error" sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'background.paper' }} onClick={handleRemoveImage}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', height: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'grey.100', mb: 2 }}>
                      <ImageNotSupportedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                  </Box>
                )}
                <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} disabled={uploadingImage}>
                  {currentImageUrl ? "Change Image" : "Upload Image"}
                  <input type="file" hidden accept="image/*" onChange={handleImageFileChange} />
                </Button>
                {uploadingImage && <CircularProgress size={24} sx={{ mt: 1 }} />}
              </Box>

              {/* Is Featured Toggle */}
              <FormControlLabel
                control={<Switch checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />}
                label="Feature this event (for special display)"
                sx={{ mt: 1 }}
              />

              {/* Recurrence Section */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Recurrence</Typography>
              <FormControlLabel
                control={<Checkbox checked={isRepeating} onChange={(e) => setIsRepeating(e.target.checked)} />}
                label="Does this event repeat?"
              />

              {isRepeating && (
                <Box sx={{ ml: 3, borderLeft: '2px solid #eee', pl: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Repeats</InputLabel>
                    <Select
                      value={repeatFrequency}
                      label="Repeats"
                      onChange={(e) => {
                        setRepeatFrequency(e.target.value);
                        setMonthlyWeek(0); // Reset monthly week when frequency changes
                      }}
                    >
                      <MenuItem value="weekly">Weekly</MenuItem>
                      {/* *** START OF FIX: Add Biweekly Option *** */}
                      <MenuItem value="biweekly">Biweekly</MenuItem>
                      {/* *** END OF FIX *** */}
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Annually</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {/* NEW: Dropdown for selecting week number for monthly recurrence */}
                  {repeatFrequency === 'monthly' && startAt && (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Week of Month</InputLabel>
                      <Select
                        value={monthlyWeek}
                        label="Week of Month"
                        onChange={(e) => setMonthlyWeek(e.target.value)}
                      >
                        <MenuItem value={0}>Every {formatFns(startAt, 'EEEE')}</MenuItem>
                        <MenuItem value={1}>First {formatFns(startAt, 'EEEE')}</MenuItem>
                        <MenuItem value={2}>Second {formatFns(startAt, 'EEEE')}</MenuItem>
                        <MenuItem value={3}>Third {formatFns(startAt, 'EEEE')}</MenuItem>
                        <MenuItem value={4}>Fourth {formatFns(startAt, 'EEEE')}</MenuItem>
                        <MenuItem value={-1}>Last {formatFns(startAt, 'EEEE')}</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  <DatePicker
                    label="Ends On (Optional)"
                    value={repeatEndsAt}
                    onChange={(newValue) => setRepeatEndsAt(newValue)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                    enableAccessibleFieldDOMStructure={false}
                  />
                </Box>
              )}


              {isEventRegistrationEnabledForChurch && isLeadership && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Registration Settings</Typography>
                  <FormControlLabel
                    control={<Checkbox checked={isRegistrationEnabled} onChange={(e) => setIsRegistrationEnabled(e.target.checked)} />}
                    label="Enable Registration for this Event"
                  />

                  {isRegistrationEnabled && (
                    <Box sx={{ ml: 3, borderLeft: '2px solid #eee', pl: 2 }}>
                      <TextField
                        margin="normal" fullWidth label="Capacity (Number of Registrations)" type="number" value={capacity}
                        onChange={(e) => setCapacity(e.target.value)} inputProps={{ min: 1 }}
                      />
                      <DatePicker
                        label="Registration Deadline (Optional)" value={registrationDeadline}
                        onChange={(newValue) => setRegistrationDeadline(newValue)}
                        slots={{ textField: TextField }}
                        slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                        enableAccessibleFieldDOMStructure={false}
                      />
                      <FormControlLabel
                        control={<Checkbox checked={isPaidEvent} onChange={(e) => setIsPaidEvent(e.target.checked)} />}
                        label="Paid Event"
                      />
                      {isPaidEvent && (
                        <TextField
                          margin="normal" fullWidth label="Ticket Price" type="number" value={ticketPrice}
                          onChange={(e) => setTicketPrice(e.target.value)} inputProps={{ min: 0.01, step: 0.01 }}
                        />
                      )}

                      <FormControl fullWidth margin="normal">
                        <InputLabel id="event-pocs-label">Event Point of Contact(s)</InputLabel>
                        <Select
                          labelId="event-pocs-label" multiple value={eventPOCs}
                          onChange={(e) => setEventPOCs(e.target.value)}
                          label="Event Point of Contact(s)"
                          renderValue={(selected) => selected.map(uid => {
                              const member = churchMembers.find(m => m.id === uid);
                              return member ? `${member.firstName} ${member.lastName}` : uid;
                          }).join(', ')}
                        >
                          {churchMembers.map((member) => (
                            <MenuItem key={member.id} value={member.id}>
                              {member.firstName} {member.lastName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {event?.id && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Current Registrations: {currentRegistrations}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </>
          ) : ( // Read-only view
            <>
              <Typography variant="h5" component="div" sx={{ mb: 2 }}>{title}</Typography>
              {currentImageUrl && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <img src={currentImageUrl} alt="Event" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
                </Box>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}><strong>Starts:</strong> {formatDisplayDate(startAt, event?.isAllDay)}</Typography>
              {endAt && <Typography variant="body1" sx={{ mb: 2 }}><strong>Ends:</strong> {formatDisplayDate(endAt, event?.isAllDay)}</Typography>}
              {location && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Location:</strong>{" "}
                  <Link
                    href={`https://maps.google.com/?q=${encodeURIComponent(location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {location}
                  </Link>
                </Typography>
              )}
              {virtualLink && <Typography variant="body1" sx={{ mb: 2 }}><strong>Virtual Link:</strong> <Link href={virtualLink} target="_blank" rel="noopener noreferrer">{virtualLink}</Link></Typography>}
              {description && <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>{description}</Typography>}

              {recurrenceRules && recurrenceRules.length > 0 && startAt && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed #eee' }}>
                  <Typography variant="h6">Recurrence</Typography>
                  {(() => {
                    const ruleText = recurrenceRules[0];
                    if (ruleText.includes('FREQ=YEARLY')) {
                      return <Typography variant="body2">Repeats every year on {formatFns(startAt, 'MMMM do')}.</Typography>;
                    }
                    if (ruleText.includes('FREQ=MONTHLY')) {
                        const bydayMatch = ruleText.match(/BYDAY=(-?\d+)([A-Z]{2})/);
                        if (bydayMatch) {
                            const weekNum = parseInt(bydayMatch[1], 10);
                            const weekMap = {1: 'first', 2: 'second', 3: 'third', 4: 'fourth', '-1': 'last'};
                            return <Typography variant="body2">Repeats on the {weekMap[weekNum]} {formatFns(startAt, 'EEEE')} of every month.</Typography>;
                        }
                        return <Typography variant="body2">Repeats every month on day {formatFns(startAt, 'do')}.</Typography>;
                    }
                    if (ruleText.includes('FREQ=WEEKLY')) {
                      const bydayMatch = ruleText.match(/BYDAY=([A-Z,]+)/);
                      const days = bydayMatch ? bydayMatch[1].split(',').map(d => DAYS_OF_WEEK.find(item => item.value === d)?.label || d).join(', ') : formatFns(startAt, 'EEEE');
                      
                      // *** START OF FIX: Display Biweekly Rule ***
                      if (ruleText.includes('INTERVAL=2')) {
                        return <Typography variant="body2">Repeats biweekly on {days}.</Typography>;
                      }
                      // *** END OF FIX ***
                      return <Typography variant="body2">Repeats weekly on {days}.</Typography>;
                    }
                    return <Typography variant="body2">This event repeats.</Typography>;
                  })()}
                  {repeatEndsAt && (
                    <Typography variant="body2">
                      Ends On: {formatDisplayDate(repeatEndsAt, true)}
                    </Typography>
                  )}
                </Box>
              )}


              {isEventRegistrationEnabledForChurch && event?.isRegistrationEnabled && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed #eee' }}>
                  <Typography variant="h6">Registration Information</Typography>
                  <Typography variant="body1">Capacity: {event.currentRegistrations || 0} / {event.capacity || 'Unlimited'}</Typography>
                  {event.registrationDeadline && <Typography variant="body1">Deadline: {formatDisplayDate(event.registrationDeadline.toDate(), false)}</Typography>}
                  {event.isPaidEvent && (
                    <>
                      <Typography variant="body1">Price: ${event.ticketPrice?.toFixed(2) || '0.00'}</Typography>
                      {currentUserRegistrationStatus && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <Typography variant="body1" sx={{ mr: 1 }}>Your Status:</Typography>
                          <Chip
                            label={currentUserRegistrationStatus.paymentStatus === 'paid' ? 'Paid' : 'Payment Pending'}
                            color={currentUserRegistrationStatus.paymentStatus === 'paid' ? 'success' : 'warning'}
                            size="small"
                            icon={currentUserRegistrationStatus.paymentStatus === 'paid' ? <PaidIcon /> : <PendingIcon />}
                          />
                        </Box>
                      )}
                    </>
                  )}
                  {event.eventPOCs && event.eventPOCs.length > 0 && (
                    <Typography variant="body1">
                      <strong>Contact(s):</strong> {getPOCNames(event.eventPOCs)}
                    </Typography>
                  )}

                  {currentUser && !isLeadership && !isPOC && (
                    currentUserRegistrationStatus ? (
                      <Button
                        variant="outlined"
                        color="error"
                        sx={{ mt: 2 }}
                        onClick={handleCancelRegistration}
                        disabled={isRegistering || (currentUserRegistrationStatus.paymentStatus === 'paid')}
                      >
                        {isRegistering ? <CircularProgress size={24} /> : 'Cancel Registration'}
                      </Button>
                    ) : (
                      <Button variant="contained" sx={{ mt: 2 }} onClick={handleRegisterNow} disabled={isRegistering}>
                        {isRegistering ? <CircularProgress size={24} /> : 'Register Now'}
                      </Button>
                    )
                  )}

                  {(isAdmin || isPOC) && (
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      component={RouterLink}
                      to={`/events/${event.id}/registrations`}
                    >
                      Manage Registrations
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {isLeadership && event ? (
              <Box>
                <Button onClick={() => onDelete(event.id)} color="error" sx={{ mr: 1 }}>Delete Series</Button>
                {event.recurrenceRules && event.recurrenceRules.length > 0 && (
                    <Button onClick={() => onDelete(event.id, selectedOccurrenceStart)} color="warning">
                        Delete Occurrence
                    </Button>
                )}
              </Box>
            ) : <div />}
            <Box>
              {event && (
                  <Button
                    variant="outlined" startIcon={<CalendarMonthIcon />}
                    onClick={handleAddToCalendar} sx={{ mr: 1 }}
                  >
                    Add to Calendar
                  </Button>
              )}
              <Button onClick={onClose} sx={{ mr: 1 }} disabled={uploadingImage}>
                {isLeadership ? 'Cancel' : 'Close'}
              </Button>
              {isLeadership && <Button onClick={handleSave} variant="contained" disabled={uploadingImage}>Save</Button>}
            </Box>
          </Box>
        </LocalizationProvider>
      </Box>
    </Modal>
  );
}

export default EventModal;